import React, { useEffect } from 'react';
import { FieldArray, useFormik, FormikProvider } from 'formik';
import { Button, Box } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContentContext } from '@qbe/dfc-content';

import { FormStepContentProps } from 'types';
import { EmploymentFormFields, StepMethod, useFormState, EmploymentData } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';

import EmploymentStatus from './sections/EmploymentStatus';
import EmploymentEdit from './sections/EmploymentEdit';
import EmploymentPreview from './sections/EmploymentPreview';
import validationSchema from './schema';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import { useContentComplaintPartners } from 'utils/content';

type DivRefs = { [key: string]: React.RefObject<HTMLDivElement> };

// Used formik for array component handling
const EmploymentForm = ({ onComplete, onStart }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData, updateForm } = useFormState();

  const { getTextContent } = useContentComplaintPartners({
    context: useContentContext(),
  });

  const [mode, setMode] = React.useState<StepMethod>('EmploymentEdit');
  const [selectedIndex, setSelectedIndex] = React.useState<string>('');

  const initialValues: EmploymentFormFields = {
    employmentData: formData?.employmentData ? formData.employmentData : [],
    employmentStatus: formData?.employmentStatus ? formData.employmentStatus : '',
  };

  const handleSubmit = (values: EmploymentFormFields) => {
    onComplete({ ...formData, employmentData: values.employmentData, employmentStatus: values.employmentStatus });

    if (values.employmentStatus === 'no') {
      updateForm({
        employmentData: [],
      });
    }
  };

  const formikBag = useFormik<EmploymentFormFields>({
    initialValues,
    validationSchema,
    validateOnMount: false,
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    formikBag.dirty && onStart();
  }, [formikBag.dirty, onStart]);

  const isEdit = mode === 'EmploymentEdit';
  const isDisabled = !validationSchema.isValidSync(formikBag.values);
  const isEmployed = formikBag.values.employmentStatus === 'yes';
  const employmentData = formikBag.values.employmentData;

  const refs: DivRefs = employmentData.reduce((accumulator: DivRefs, value: EmploymentData) => {
    accumulator[value.id] = React.createRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>;
    return accumulator;
  }, {});

  React.useEffect(() => {
    if (!isEdit) window.scrollTo(0, 0);

    if (isEdit && selectedIndex && refs[selectedIndex].current?.offsetTop) {
      window.scrollTo(0, Number(refs[selectedIndex].current?.offsetTop));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, isEdit]);

  const handleEdit = (id: string) => {
    setMode('EmploymentEdit');
    setSelectedIndex(id);
  };

  return (
    <FormikProvider value={formikBag}>
      <form onSubmit={formikBag.handleSubmit} noValidate>
        {isEdit && (
          <>
            <FormCard variant="pageTitle" title={getTextContent('fha.employment.details.heading')} description="" />

            <FormSpacer type="pagesection" />

            <FieldArray name="employmentData">
              {(arrayHelpers) => (
                <>
                  <EmploymentStatus arrayHelpers={arrayHelpers} />

                  {employmentData.map((field, index) => (
                    <EmploymentEdit key={field.id} ref={refs[field.id]} index={index} arrayHelpers={arrayHelpers} />
                  ))}
                </>
              )}
            </FieldArray>
          </>
        )}

        {!isEdit && (
          <>
            <FormCard
              variant="pageTitle"
              title={getTextContent('fha.employment.details.preview.heading')}
              description=""
            />

            {employmentData.map((field) => (
              <EmploymentPreview key={field.id} field={field} onEdit={() => handleEdit(field.id)} />
            ))}
          </>
        )}

        {isEdit && isEmployed && (
          <>
            <FormSpacer type="pagesection" />
            <Box display="flex" justifyContent="center">
              <Button
                label="Continue"
                type="button"
                onClick={() => {
                  setMode('EmploymentPreview');
                  setSelectedIndex('');
                }}
                disabled={isDisabled}
              />
            </Box>
          </>
        )}

        {!isEdit && (
          <>
            <FormSpacer type="pagesection" />
            <Box display="flex" justifyContent="center">
              <Button label="Continue" type="submit" disabled={isDisabled} />
            </Box>
          </>
        )}
      </form>
    </FormikProvider>
  );
};

export default EmploymentForm;
